/**
 * Step 9 — deterministic camera hint (owner: rules team). The renderer smooths, shakes and punches it; the sim only
 * says WHERE to look:
 *  - PRE_SNAP / CONVERSION: the line of scrimmage, nudged downfield.
 *  - Mirage decoy up: CameraFocus.SPLIT, centred between the real carrier and the decoy (never gives it away).
 *  - Ball held: the carrier (PLAYER0/1) with look-ahead proportional to his run speed.
 *  - Ball in the air / dead: the ball with look-ahead along its flight.
 *  - Zoom: out for high balls (CAMERA.zoomOutPerYard, min CAMERA.minZoom) and out far enough to keep both heads in
 *    frame around the target where possible.
 * Pure arithmetic on the state (deterministic, allocation-free).
 */
import { CAMERA, MOVE, PLAY_MAX_X, PLAY_MIN_X } from '@hf/shared';
import { BallState, CameraFocus, Phase } from './enums';
import type { SimState } from './state';

/** Ball speed (yd/s) at which the in-flight look-ahead reaches its full CAMERA.lookAhead. */
const FLIGHT_LOOK_SPEED = 20;
/** Margin kept around each head when zooming out to fit both (yards). */
const FIT_MARGIN = 3;
/** Resting camera height (yards) and how much of the target's height above REST_Y the camera follows. */
const REST_Y = 2;
const FOLLOW_Y = 0.6;

export const updateCameraHint = (s: SimState): void => {
  const cam = s.camera;
  const r = s.rules;
  const b = s.ball;
  const p0 = s.players[0];
  const p1 = s.players[1];
  let tx: number;
  let ty: number;
  let focus: CameraFocus;
  let decoyOwner = -1;
  if (p0.special.decoyActive) decoyOwner = 0;
  else if (p1.special.decoyActive) decoyOwner = 1;

  if (r.phase === Phase.PRE_SNAP || r.phase === Phase.CONVERSION) {
    tx = r.los + r.attackDir * CAMERA.lookAhead * 0.5;
    ty = b.y;
    focus = CameraFocus.BALL;
  } else if (decoyOwner >= 0) {
    const real = s.players[decoyOwner as 0 | 1];
    tx = (real.x + real.special.decoyX) * 0.5;
    ty = (real.y + real.special.decoyY) * 0.5;
    focus = CameraFocus.SPLIT;
  } else if (b.state === BallState.HELD && b.holder >= 0) {
    const c = s.players[b.holder as 0 | 1];
    // look-ahead ∝ run speed, clamped to ±1 inline (a helper returning the double would box it every tick)
    tx = c.x + Math.min(Math.max(c.vx / MOVE.runSpeed, -1), 1) * CAMERA.lookAhead;
    ty = c.y;
    focus = b.holder === 0 ? CameraFocus.PLAYER0 : CameraFocus.PLAYER1;
  } else {
    const inFlight = b.state === BallState.AIR || b.state === BallState.LOOSE;
    tx = b.x + Math.min(Math.max(b.vx / FLIGHT_LOOK_SPEED, -1), 1) * CAMERA.lookAhead * (inFlight ? 1 : 0);
    ty = b.y;
    focus = CameraFocus.BALL;
  }
  // Math.min/max (not `if (tx < MIN) tx = MIN`): merging a double with an imported integer constant would make
  // the optimizer box `tx` into a fresh HeapNumber every tick (the only steady allocation of the camera).
  tx = Math.min(Math.max(tx, PLAY_MIN_X), PLAY_MAX_X);

  // Zoom out for a high ball, and far enough to keep both heads (and a visible decoy) in frame around tx.
  // Every merge is Math.min/max: no double is ever merged with a constant (zero steady-state allocation).
  const heightZoom = CAMERA.baseZoom - Math.max(b.y, 0) * CAMERA.zoomOutPerYard;
  const decoyX = decoyOwner >= 0 ? s.players[decoyOwner as 0 | 1].special.decoyX : tx;
  const half = Math.max(Math.abs(p0.x - tx), Math.abs(p1.x - tx), Math.abs(decoyX - tx)) + FIT_MARGIN;
  const fit = (CAMERA.viewWidth * 0.5) / half;
  const zoom = Math.min(Math.max(Math.min(heightZoom, fit), CAMERA.minZoom), CAMERA.baseZoom);

  cam.x = tx;
  cam.y = REST_Y + Math.max(ty - REST_Y, 0) * FOLLOW_Y;
  cam.zoom = zoom;
  cam.focus = focus;
};
